import { useState } from "react";
import { KeyRound, Loader2, LogOut } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { changePassword, formatApiError } from "@/lib/api";

const validate = (cur, next, confirm) => {
  if (next.length < 8) return "New password must be at least 8 characters";
  if (next === cur) return "New password must be different from the temporary one";
  if (next !== confirm) return "Passwords don't match";
  return "";
};

export default function ForcePasswordChange() {
  const { user, refresh, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const msg = validate(cur, next, confirm);
    if (msg) return setError(msg);
    setError("");
    setBusy(true);
    try {
      await changePassword(cur, next);
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  const field = (id, label, value, set, auto) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-slate-400">{label}</Label>
      <Input id={id} data-testid={id} type="password" autoComplete={auto} required value={value} onChange={(e) => set(e.target.value)} className="border-slate-700/60 bg-slate-900/60 text-slate-100" />
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#080C14] px-6">
      <form data-testid="force-password-form" onSubmit={submit} className="w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-blue-500/30 bg-blue-500/10">
          <KeyRound className="h-5 w-5 text-blue-400" />
        </div>
        <div>
          <h1 className="font-['Outfit'] text-4xl font-bold tracking-tight text-slate-100">Choose your password</h1>
          <p className="mt-2 text-sm text-slate-500">Hi {user.name || user.email}, replace the temporary password you were given before continuing.</p>
        </div>
        {field("force-current-password", "Temporary password", cur, setCur, "current-password")}
        {field("force-new-password", "New password (min 8 characters)", next, setNext, "new-password")}
        {field("force-confirm-password", "Confirm new password", confirm, setConfirm, "new-password")}
        {error && <p data-testid="force-password-error" className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</p>}
        <div className="flex items-center gap-3">
          <Button data-testid="force-password-submit" type="submit" disabled={busy} className="flex-1 bg-blue-600 font-semibold text-white hover:bg-blue-700">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Set password & continue
          </Button>
          <Button data-testid="force-password-logout" type="button" variant="ghost" onClick={logout} className="text-slate-400 hover:text-slate-200">
            <LogOut className="mr-1.5 h-4 w-4" />Sign out
          </Button>
        </div>
      </form>
    </div>
  );
}
